import type { JSX, KeyboardEvent, MouseEvent, TouchEvent } from 'react';
import styled from 'styled-components';
import { Text, theme, Toggle, ToggleSwitch } from '@frameio/vapor';
import { IconLock } from '@frameio/vapor-icons';
import { IconLockFeatureGate } from '../../icons';

type RestrictedAccessToggleProps = {
  canManageAccess?: boolean;
  isRestricted: boolean;
  onChange: () => void;
  isFeatureGated: boolean;
  isFeatureGateActive: boolean;
  isLastToggle?: boolean;
  title: string;
  description: string | JSX.Element;
};

export const RestrictedAccessToggle = ({
  canManageAccess = true,
  isRestricted,
  isFeatureGated,
  isFeatureGateActive,
  isLastToggle = true,
  onChange,
  title,
  description,
}: RestrictedAccessToggleProps) => {
  // Handler to prevent event bubbling and ensure immediate response on the toggle
  const handleToggleClick = (e: MouseEvent | TouchEvent | KeyboardEvent) => {
    if (isFeatureGated || !canManageAccess) return;
    e.preventDefault();
    e.stopPropagation();
    onChange();
  };

  return (
    <ToggleWrapper $isFocused={isFeatureGateActive} $isLast={isLastToggle}>
      <Toggle
        block={true}
        checked={isRestricted}
        data-testid="restricted-toggle"
        disabled={isFeatureGated || !canManageAccess}
        onChange={onChange}
      >
        <ToggleContainer
          onClick={handleToggleClick}
          onKeyDown={handleToggleClick}
          onTouchStart={handleToggleClick}
        >
          <ToggleInfo>
            {isFeatureGated ? (
              <IconLockFeatureGate isActive={true} />
            ) : (
              <IconLock color={theme.colors.icon.primary} size={24} />
            )}
            <ToggleLabels>
              <Text variant="body">{title}</Text>
              <Text
                color="tertiary"
                style={{ textAlign: 'left' }}
                variant="secondary"
              >
                {description}
              </Text>
            </ToggleLabels>
          </ToggleInfo>
          <ToggleSwitch />
        </ToggleContainer>
      </Toggle>
    </ToggleWrapper>
  );
};

const ToggleWrapper = styled.div<{
  $isFocused?: boolean;
  $isLast?: boolean;
}>`
  background: ${theme.colors.background.card};
  border-top-left-radius: ${theme.radii['6']};
  border-top-right-radius: ${theme.radii['6']};
  padding-block: ${theme.spaces[16]};
  padding-inline: ${theme.spaces[12]} ${theme.spaces[16]};
  width: 100%;

  ${(p) =>
    p.$isLast &&
    `border-bottom-left-radius: ${theme.radii['6']};
     border-bottom-right-radius: ${theme.radii['6']};`}

  ${(p) =>
    p.$isFocused &&
    `box-shadow: inset 0 0 0 1.5px ${theme.colors.border.interactive.hover};`}
`;

const ToggleContainer = styled.div`
  align-items: center;
  display: grid;
  gap: ${theme.spaces[16]};
  grid-template-columns: 1fr auto;
  width: 100%;
`;

const ToggleInfo = styled.div`
  align-items: center;
  display: grid;
  gap: ${theme.spaces[8]};
  grid-template-columns: auto 1fr;
`;

const ToggleLabels = styled.div`
  align-items: start;
  display: flex;
  flex-direction: column;
  gap: ${theme.spaces[2]};
  justify-content: start;
  text-align: start;
`;
